import { ChoiceCard } from "@core/components/ChoiceCard";
import { List, ListRow } from "@core/components/List";
import { Value } from "@core/components/Value";
import type { StepProps } from "@core/registry";
import { DescriptionTab } from "./sheet";
import { page, useSm } from "./view";

/** Step 10: derived values, free masteries and spells, languages, name and background (GRW p. 25–26). */
export function FinishingStep({ step }: StepProps) {
  const { view, apply, character } = useSm();
  const choices = character.evaluation.openChoices.filter((c) => c.step === step);
  const masteryChoices = choices.filter((c) => c.id.startsWith("meisterschaft."));
  const spellChoices = choices.filter((c) => c.id.startsWith("zauber."));
  const other = choices.filter((c) => !masteryChoices.includes(c) && !spellChoices.includes(c));
  const freeMasteries = view.skills.flatMap((s) => s.masteries.filter((m) => m.free).map((m) => ({ ...m, skillName: s.name })));
  const magicSchools = view.skills.filter((s) => s.category === "magie");

  return (
    <div className="stack">
      <section className="card stack">
        <h3>Abgeleitete Werte</h3>
        <p className="muted small">Ergeben sich aus Attributen, Rasse und Ausrüstung; antippen zeigt die Herleitung.</p>
        <div className="tiles">
          {view.derived.map((d) => (
            <Value key={d.id} valueKey={d.value} label={d.short} showLabel />
          ))}
          <Value valueKey="splitterpunkte" label="SP" showLabel />
        </div>
      </section>

      <section className="card stack">
        <h3>Freie Meisterschaften</h3>
        <p className="muted small">Jede Fertigkeit mit 6 Punkten bringt eine freie Meisterschaft der Schwelle 1 (GRW S. 25).</p>
        <List aria-label="Freie Meisterschaften">
          {freeMasteries.length === 0 && masteryChoices.length === 0 && <li className="list-empty">Keine Fertigkeit hat 6 Punkte, daher keine freie Meisterschaft.</li>}
          {freeMasteries.map((m, i) => {
            const stored = view.finishing.masteries.find((f) => f.id === m.id && (f.skill ?? null) === (view.skills.find((s) => s.name === m.skillName)?.id ?? null));
            return (
              <ListRow
                key={`${m.id}-${i}`}
                title={m.name}
                badges={m.param ? <span className="badge">{m.param}</span> : undefined}
                subtitle={
                  <>
                    {m.skillName} · {page(m.source)}
                    {m.missing && <> · erst nutzbar nach Erwerb von {m.missing}</>}
                  </>
                }
                actions={
                  stored && (
                    <button type="button" className="link danger" onClick={() => void apply({ type: "sm.removeFreeMastery", payload: { index: stored.index } })}>
                      Entfernen
                    </button>
                  )
                }
              />
            );
          })}
        </List>
        {masteryChoices.map((choice) => (
          <ChoiceCard key={choice.id} choice={choice} />
        ))}
      </section>

      {(magicSchools.some((s) => view.spells.some((sp) => sp.school === s.id)) || spellChoices.length > 0) && (
        <section className="card stack">
          <h3>Freie Zauber</h3>
          <p className="muted small">Jede Magieschule bringt freie Zauber: Grad 0 ab 1 Punkt, Grad 1 ab 3, Grad 2 ab 6 Punkten (GRW S. 26).</p>
          <List aria-label="Zauber">
            {view.spells.map((spell, i) => {
              const stored = view.finishing.spells.find((f) => f.id === spell.id && f.school === spell.school);
              return (
                <ListRow
                  key={`${spell.id}-${i}`}
                  title={spell.name}
                  badges={spell.free ? <span className="badge">frei</span> : undefined}
                  subtitle={`${spell.schoolName} Grad ${spell.grade ?? "?"} · ${spell.origin} · ${page(spell.source)}`}
                  actions={
                    stored && (
                      <button type="button" className="link danger" onClick={() => void apply({ type: "sm.removeFreeSpell", payload: { index: stored.index } })}>
                        Entfernen
                      </button>
                    )
                  }
                />
              );
            })}
          </List>
          {spellChoices.map((choice) => (
            <ChoiceCard key={choice.id} choice={choice} />
          ))}
        </section>
      )}

      {other.length > 0 && (
        <section className="stack">
          <h3 className="section-title">Noch festzulegen</h3>
          {other.map((choice) => (
            <ChoiceCard key={choice.id} choice={choice} />
          ))}
        </section>
      )}

      <section className="card stack">
        <h3>Sprachen</h3>
        <List aria-label="Sprachen">
          {view.languages.map((l) => (
            <ListRow key={l.id} title={l.name} subtitle={l.origin} />
          ))}
        </List>
        <p className="muted small">Weitere Sprachen kosten Start-Erfahrung (Schritt 9) oder später Erfahrungspunkte.</p>
      </section>

      <section className="stack">
        <h3 className="section-title">Aussehen und Hintergrund</h3>
        <DescriptionTab />
      </section>

      <section className="card stack" aria-label="Abschluss des Feinschliffs">
        {view.finishing.confirmed ? (
          <div className="row spread">
            <span>✓ Feinschliff abgeschlossen</span>
            <button type="button" className="link" onClick={() => void apply({ type: "sm.confirmFinishing", payload: { confirmed: false } })}>
              Wieder öffnen
            </button>
          </div>
        ) : (
          <>
            <p className="muted small">
              Sind abgeleitete Werte, freie Meisterschaften und Zauber, Sprachen und Beschreibung so, wie du sie haben willst? Dann schließe den Schritt ab.
              {choices.length > 0 && " Offene Wahlen bleiben offen, bis du sie getroffen hast."}
            </p>
            <div className="actions">
              <button type="button" className="button primary" onClick={() => void apply({ type: "sm.confirmFinishing", payload: { confirmed: true } })}>
                Feinschliff abschließen
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
